import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '../../components/common/Button/Button';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../components/common/DecoratedHeader/DecoratedHeader';
import Icon from '../../components/common/Icon/Icon';
import ImageSourcePickerModal from '../../components/common/ImageSourcePickerModal/ImageSourcePickerModal';
import Logo from '../../components/common/Logo/Logo';
import { createLocalMealPhoto, useMealTracking } from '../../contexts/MealTrackingContext';
import CheckoutContainer from '../Checkout/components/CheckoutContainer';
import pageStyles from '../Checkout/CheckoutPage.module.css';
import fupStyles from '../Fup/Fup.module.css';
import MealFormModal from './components/MealFormModal';
import MealPhotoViewer from './components/MealPhotoViewer';
import { MEAL_TRACKING_BASE_PATH } from './mealTracking.constants';
import {
  formatMealDisplayTime,
  formatMealLongDate,
  resolveMealTitle,
  toDateOnly,
} from './mealTracking.utils';
import styles from './MealTracking.module.css';

const MealDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mealId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const selectedDate = toDateOnly(searchParams.get('date') || '');
  const {
    getMealById,
    deleteMeal,
    updateMeal,
    removePhotoFromMeal,
  } = useMealTracking();

  const meal = getMealById(mealId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [replacePhotoId, setReplacePhotoId] = useState<string | null>(null);
  const [pendingDeleteMeal, setPendingDeleteMeal] = useState(false);
  const [pendingRemovePhotoId, setPendingRemovePhotoId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const isEditRoute = /\/editar\/?$/.test(location.pathname);

  const listPath = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedDate || meal?.date) {
      params.set('date', selectedDate || meal?.date || '');
    }
    const query = params.toString();
    return query ? `${MEAL_TRACKING_BASE_PATH}?${query}` : MEAL_TRACKING_BASE_PATH;
  }, [meal?.date, selectedDate]);

  const editPath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('date', meal?.date || selectedDate);
    return `${MEAL_TRACKING_BASE_PATH}/${mealId}/editar?${params.toString()}`;
  }, [meal?.date, mealId, selectedDate]);

  useEffect(() => {
    if (!meal) {
      navigate(listPath, { replace: true });
    }
  }, [listPath, meal, navigate]);

  useEffect(() => {
    if (isEditRoute && meal) {
      setFormOpen(true);
    }
  }, [isEditRoute, meal]);

  if (!meal) {
    return null;
  }

  const openEditModal = () => {
    setFormOpen(true);
    navigate(editPath);
  };

  const closeFormModal = () => {
    setFormOpen(false);
    if (isEditRoute) {
      const params = new URLSearchParams();
      params.set('date', meal.date || selectedDate);
      navigate(`${MEAL_TRACKING_BASE_PATH}/${meal.id}?${params.toString()}`, { replace: true });
    }
  };

  const handleAddOrReplacePhotos = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    if (replacePhotoId) {
      const replacement = createLocalMealPhoto(files[0]);
      updateMeal(meal.id, {
        date: meal.date,
        time: meal.time,
        categoryId: meal.categoryId,
        customTitle: meal.customTitle ?? '',
        description: meal.description,
        photos: meal.photos.map((photo) => (photo.id === replacePhotoId ? replacement : photo)),
      });
      setReplacePhotoId(null);
      return;
    }

    updateMeal(meal.id, {
      date: meal.date,
      time: meal.time,
      categoryId: meal.categoryId,
      customTitle: meal.customTitle ?? '',
      description: meal.description,
      photos: [...meal.photos, ...files.map(createLocalMealPhoto)],
    });
  };

  return (
    <CheckoutContainer>
      <div className={styles.logoHeader}>
        <Logo className={styles.formLogo} />
        <DecoratedHeader
          className={styles.formTitle}
          title="Detalhes da refeição"
        />
        <button type="button" className={styles.backLink} onClick={() => navigate(listPath)}>
          <Icon name="arrow_back" size={18} />
          Voltar para o diário
        </button>
      </div>

      <div className={styles.centeredLayout}>
        <article className={`${pageStyles.checkoutCard} ${styles.centeredCard}`}>
          <MealPhotoViewer
            photos={meal.photos}
            showEmptyActions
            onAddPhoto={() => {
              setReplacePhotoId(null);
              setPickerOpen(true);
            }}
          />

          <div className={styles.photoActions}>
            <button
              type="button"
              className={styles.photoActionButton}
              onClick={() => {
                setReplacePhotoId(null);
                setPickerOpen(true);
              }}
              aria-label="Adicionar foto"
              title="Adicionar foto"
            >
              <Icon name="add_a_photo" size={18} color="var(--color-brand-primary)" />
              <span>Adicionar</span>
            </button>
            <button
              type="button"
              className={styles.photoActionButton}
              onClick={() => {
                setReplacePhotoId(meal.photos[0]?.id ?? null);
                setPickerOpen(true);
              }}
              disabled={meal.photos.length === 0}
              aria-label="Trocar foto"
              title="Trocar foto"
            >
              <Icon name="change_circle" size={18} color="var(--color-brand-primary)" />
              <span>Trocar</span>
            </button>
            <button
              type="button"
              className={`${styles.photoActionButton} ${styles.photoActionDanger}`}
              onClick={() => setPendingRemovePhotoId(meal.photos[0]?.id ?? null)}
              disabled={meal.photos.length === 0}
              aria-label="Remover foto"
              title="Remover foto"
            >
              <Icon name="delete" size={18} color="var(--color-error)" />
              <span>Remover</span>
            </button>
          </div>

          <div className={styles.infoList}>
            <div className={styles.infoRow}>
              <Icon name="restaurant" size={20} color="var(--color-sidebar-icon)" />
              <div>
                <p className={styles.infoLabel}>Tipo de refeição</p>
                <p className={styles.infoValue}>{resolveMealTitle(meal)}</p>
              </div>
            </div>
            <div className={styles.infoRow}>
              <Icon name="schedule" size={20} color="var(--color-sidebar-icon)" />
              <div>
                <p className={styles.infoLabel}>Horário</p>
                <p className={styles.infoValue}>{formatMealDisplayTime(meal.time)}</p>
              </div>
            </div>
            <div className={styles.infoRow}>
              <Icon name="calendar_today" size={20} color="var(--color-sidebar-icon)" />
              <div>
                <p className={styles.infoLabel}>Data</p>
                <p className={styles.infoValue}>{formatMealLongDate(meal.date)}</p>
              </div>
            </div>
            <div className={styles.infoRow}>
              <Icon name="description" size={20} color="var(--color-sidebar-icon)" />
              <div>
                <p className={styles.infoLabel}>Descrição</p>
                <p className={styles.infoValue}>{meal.description}</p>
              </div>
            </div>
          </div>

          <div className={fupStyles.footerActions}>
            <div className={styles.formActionsRow}>
              <Button
                type="button"
                variant="outline"
                size="app"
                fullWidth
                onClick={() => setPendingDeleteMeal(true)}
              >
                Excluir
              </Button>
              <Button
                type="button"
                variant="primary"
                size="app"
                fullWidth
                iconRight="edit"
                onClick={openEditModal}
              >
                Editar
              </Button>
            </div>
          </div>
        </article>
      </div>

      <MealFormModal
        open={formOpen}
        mealId={meal.id}
        defaultDate={meal.date || selectedDate}
        onClose={closeFormModal}
        onSaved={(savedMeal, meta) => {
          setFormOpen(false);
          const params = new URLSearchParams({ date: savedMeal.date });
          navigate(`${MEAL_TRACKING_BASE_PATH}?${params.toString()}`, {
            replace: true,
            state: { mealFeedback: meta.isCreate ? 'created' : 'updated' },
          });
        }}
      />

      <ImageSourcePickerModal
        open={pickerOpen}
        title={replacePhotoId ? 'Substituir foto' : 'Adicionar foto'}
        galleryMultiple={!replacePhotoId}
        onCancel={() => {
          setPickerOpen(false);
          setReplacePhotoId(null);
        }}
        onFilesSelected={(files) => {
          setPickerOpen(false);
          handleAddOrReplacePhotos(files);
        }}
      />

      <ConfirmModal
        open={pendingDeleteMeal}
        title="Excluir refeição"
        message="Deseja excluir esta refeição do seu diário?"
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        onCancel={() => setPendingDeleteMeal(false)}
        onConfirm={() => {
          deleteMeal(meal.id);
          navigate(listPath);
        }}
      />

      <ConfirmModal
        open={Boolean(pendingRemovePhotoId)}
        title="Remover foto"
        message="Deseja remover esta foto da refeição?"
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        variant="danger"
        onCancel={() => setPendingRemovePhotoId(null)}
        onConfirm={() => {
          if (pendingRemovePhotoId) {
            removePhotoFromMeal(meal.id, pendingRemovePhotoId);
          }
          setPendingRemovePhotoId(null);
        }}
      />
    </CheckoutContainer>
  );
};

export default MealDetailPage;
